import React, { useEffect, useRef, useCallback } from 'react';
import type { ClassifyPrediction } from '../types';
import CardCandidateItem from './CardCandidateItem';

interface CardCandidateListProps {
  candidates: ClassifyPrediction[];
  onSelect: (prediction: ClassifyPrediction) => void;
  onBack?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  total?: number;
}

export default function CardCandidateList({
  candidates,
  onSelect,
  onBack,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  total,
}: CardCandidateListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 使用 Intersection Observer 监听滚动到底部
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element || !onLoadMore) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, onLoadMore]);
  if (candidates.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary">未识别到卡片，请重新上传图片</p>
        {onBack && (
          <button
            onClick={onBack}
            className="mt-4 text-sm text-primary hover:underline"
          >
            返回
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-primary">识别结果</h2>
          <p className="text-sm text-secondary mt-1">
            {total !== undefined 
              ? `共找到 ${total} 张匹配的卡片，已加载 ${candidates.length} 张`
              : '请选择您要查询的卡片'}
          </p>
        </div>
      </div>

      {/* 候选卡片网格 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {candidates.map((prediction, index) => (
          <CardCandidateItem
            key={prediction.id_label + index}
            prediction={prediction}
            onClick={onSelect}
          />
        ))}
      </div>

      {/* 加载更多触发器 */}
      <div ref={loadMoreRef} className="py-4">
        {isLoadingMore && (
          <div className="text-center">
            <div className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-secondary mt-2">加载更多...</p>
          </div>
        )}
        {!hasMore && candidates.length > 0 && total !== undefined && candidates.length >= total && (
          <p className="text-center text-sm text-secondary">已加载全部卡片</p>
        )}
      </div>
    </div>
  );
}
